import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Edit, Eye, Trash2 } from "lucide-react";
import { ConnectedUserFormModal } from "@/components/connected-users/ConnectedUserFormModal";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { format } from "date-fns";

interface ProfissionalUsuariosConectadosProps {
  professionalId: string;
}

interface ConnectedUser {
  id: string;
  name: string;
  cpf: string;
  work_area: string;
  email: string;
  phone: string | null;
  is_active: boolean;
  created_at: string;
  permissions?: string[];
}

export function ProfissionalUsuariosConectados({ professionalId }: ProfissionalUsuariosConectadosProps) {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<ConnectedUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ConnectedUser | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [deletingUser, setDeletingUser] = useState<ConnectedUser | null>(null);

  useEffect(() => {
    loadUsers();
  }, [professionalId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("connected_users")
        .select("*")
        .eq("owner_id", professionalId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error("Error loading connected users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: ConnectedUser) => {
    setSelectedUser(user);
    setShowFormModal(true);
  };

  const handleDelete = async () => {
    if (!deletingUser) return;

    try {
      const { error } = await supabase.functions.invoke('delete-connected-user', {
        body: { connectedUserId: deletingUser.id }
      });

      if (error) throw error;

      toast.success("Usuário conectado excluído com sucesso");
      setDeletingUser(null);
      loadUsers();
    } catch (error: any) {
      toast.error("Erro ao excluir usuário conectado");
    }
  };

  if (loading) {
    return <p>Carregando...</p>;
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Nenhum usuário conectado</p>
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Área</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Vinculado em</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">{user.name}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.work_area}</TableCell>
              <TableCell>
                <Badge variant={user.is_active ? "default" : "secondary"}>
                  {user.is_active ? "Ativo" : "Inativo"}
                </Badge>
              </TableCell>
              <TableCell>{format(new Date(user.created_at), "dd/MM/yyyy")}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(user)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeletingUser(user)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {showFormModal && (
        <ConnectedUserFormModal
          open={showFormModal}
          onOpenChange={setShowFormModal}
          editingUser={selectedUser ? { ...selectedUser, permissions: selectedUser.permissions || [] } : null}
          onSuccess={() => {
            loadUsers();
            setShowFormModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      <AlertDialog open={!!deletingUser} onOpenChange={() => setDeletingUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o usuário conectado{" "}
              <strong>{deletingUser?.name}</strong>?
              <br />
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}